import {useCallback, useState, type RefObject} from 'react';
import type {PhotoRequirement} from '../lib/types';
import {blurActiveElement} from '../lib/dom';
import {GUIDE_THRESHOLD, PAGE_CENTER_X, SNAP_THRESHOLD} from '../lib/preview-geometry';
import {effectivePositionModeIn} from '../lib/position-mode';

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'w' | 'e' | 'sw' | 's' | 'se';

interface DragCtx {
  selectedIds?: string[];
  groupEditId?: string | null;
  sentinelIds: Set<string>;
  sentinelMap: ReadonlyMap<string, PhotoRequirement>;
}

type ElemRefs = RefObject<Map<string, HTMLDivElement>>;

/** Drag-to-move for absolute elements, with group co-move and centre snapping. */
export function useAbsoluteDrag(opts: {
  elemRefs: ElemRefs;
  dragCtxRef: RefObject<DragCtx>;
  requirements: PhotoRequirement[];
  zoom: number;
  fillMode: boolean;
  onElementMove?: (moves: {id: string; x: number; y: number}[]) => void;
  onElementClick?: (id: string, opts?: {additive?: boolean}) => void;
}) {
  const {elemRefs, dragCtxRef, requirements, zoom, fillMode, onElementMove, onElementClick} = opts;
  const [dragState, setDragState] = useState<{
    id: string;
    x: number;
    y: number;
    showGuide: boolean;
    groupOffsets?: Map<string, {x: number; y: number}>;
  } | null>(null);
  const startDrag = useCallback(
    (e: React.MouseEvent, item: PhotoRequirement) => {
      if (e.button !== 0 || !onElementMove || fillMode) return;
      e.preventDefault();
      e.stopPropagation();
      blurActiveElement();

      const startMouseX = e.clientX;
      const startMouseY = e.clientY;
      const startElemX = item.x ?? 0;
      const startElemY = item.y ?? 0;
      const elemWidth = elemRefs.current.get(item.id)?.offsetWidth ?? 0;

      // ── Group context ─────────────────────────────────────────────────────
      // Read from ref to guarantee current values regardless of closure age.
      const {
        selectedIds: curSelectedIds,
        groupEditId: curGroupEditId,
        sentinelIds: curSentinelIds,
        sentinelMap: curSentinelMap,
      } = dragCtxRef.current;
      const sentinelGroupId =
        item.group_id && curSentinelIds.has(item.group_id) ? item.group_id : null;
      const inGroupEditMode = sentinelGroupId != null && curGroupEditId === sentinelGroupId;
      const inGroupSelectionMode =
        sentinelGroupId != null &&
        !inGroupEditMode &&
        (curSelectedIds?.includes(sentinelGroupId) ?? false);

      // Flow items can't be dragged (they have no x/y in page coords).
      // For flow items in group-selection mode, just register a click handler.
      // Use effective position mode: children of absolute groups render as absolute
      // even if their stored position_mode is "flow".
      const itemEffectiveMode = effectivePositionModeIn(item, curSentinelMap);
      if (inGroupSelectionMode && itemEffectiveMode !== 'absolute') {
        const onUp = (me: MouseEvent) => {
          const d = Math.hypot(me.clientX - startMouseX, me.clientY - startMouseY);
          if (d < 3)
            onElementClick?.(sentinelGroupId!, {
              additive: me.shiftKey || me.ctrlKey || me.metaKey,
            });
          window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mouseup', onUp);
        return;
      }

      // ── Co-move ids ───────────────────────────────────────────────────────
      const coMoveIds: string[] = (() => {
        // In group edit mode children always move independently — never co-move.
        if (inGroupEditMode) return [];
        if (item.type === 'group') {
          // Sentinel dragged directly: co-move all children.
          // Absolute group: all children have meaningful x/y (snapshotted on mode switch).
          // Flow group: only co-move absolute-mode children (flow children have no x/y).
          const isAbsoluteGroup = item.position_mode === 'absolute';
          return requirements
            .filter(
              (r) =>
                r.group_id === item.id &&
                r.type !== 'group' &&
                (isAbsoluteGroup || r.position_mode === 'absolute'),
            )
            .map((r) => r.id);
        }
        if (inGroupSelectionMode) {
          // Child in group-selection mode: co-move all siblings + sentinel.
          const sentinel = curSentinelMap.get(sentinelGroupId!);
          const isAbsoluteGroup = sentinel?.position_mode === 'absolute';
          return [
            ...requirements
              .filter(
                (r) =>
                  r.group_id === sentinelGroupId &&
                  r.id !== item.id &&
                  r.type !== 'group' &&
                  (isAbsoluteGroup || r.position_mode === 'absolute'),
              )
              .map((r) => r.id),
            sentinelGroupId!, // always move sentinel so its bounding box tracks the group
          ];
        }
        const isMultiSelected =
          (curSelectedIds?.length ?? 0) > 1 && curSelectedIds?.includes(item.id);
        if (isMultiSelected) {
          return (
            curSelectedIds?.filter((id) => {
              if (id === item.id) return false;
              const r = requirements.find((r) => r.id === id);
              return r?.position_mode === 'absolute' && r.type !== 'group';
            }) ?? []
          );
        }
        // Old-style group (group_id not pointing to a sentinel)
        if (item.group_id && !curSentinelIds.has(item.group_id)) {
          return requirements
            .filter(
              (r) =>
                r.group_id === item.group_id && r.position_mode === 'absolute' && r.id !== item.id,
            )
            .map((r) => r.id);
        }
        return [];
      })();

      const groupOffsets: Map<string, {x: number; y: number}> | undefined =
        coMoveIds.length > 0
          ? new Map(
              requirements
                .filter((r) => coMoveIds.includes(r.id))
                .map((r) => [r.id, {x: r.x ?? 0, y: r.y ?? 0}]),
            )
          : undefined;

      setDragState({id: item.id, x: startElemX, y: startElemY, showGuide: false, groupOffsets});

      const onMove = (me: MouseEvent) => {
        const dx = (me.clientX - startMouseX) / zoom;
        const dy = (me.clientY - startMouseY) / zoom;
        const rawX = startElemX + dx;
        const rawY = startElemY + dy;
        const elemCenter = rawX + elemWidth / 2;
        const dist = Math.abs(elemCenter - PAGE_CENTER_X);
        const showGuide = dist < GUIDE_THRESHOLD;
        const x = dist < SNAP_THRESHOLD ? PAGE_CENTER_X - elemWidth / 2 : rawX;

        if (groupOffsets) {
          const newOffsets = new Map<string, {x: number; y: number}>();
          groupOffsets.forEach((orig, id) => newOffsets.set(id, {x: orig.x + dx, y: orig.y + dy}));
          setDragState({id: item.id, x, y: rawY, showGuide, groupOffsets: newOffsets});
        } else {
          setDragState({id: item.id, x, y: rawY, showGuide});
        }
      };

      const onUp = (me: MouseEvent) => {
        const dxPx = me.clientX - startMouseX;
        const dyPx = me.clientY - startMouseY;
        if (Math.abs(dxPx) < 3 && Math.abs(dyPx) < 3) {
          // Route click: in group-selection mode → sentinel; otherwise → self
          const clickTarget = inGroupSelectionMode ? sentinelGroupId! : item.id;
          onElementClick?.(clickTarget, {additive: me.shiftKey || me.ctrlKey || me.metaKey});
        } else {
          const dx = dxPx / zoom;
          const dy = dyPx / zoom;
          const rawX = startElemX + dx;
          const rawY = startElemY + dy;
          const elemCenter = rawX + elemWidth / 2;
          const dist = Math.abs(elemCenter - PAGE_CENTER_X);
          const finalX = dist < SNAP_THRESHOLD ? PAGE_CENTER_X - elemWidth / 2 : rawX;
          const moves: {id: string; x: number; y: number}[] = [
            {id: item.id, x: Math.round(finalX), y: Math.round(rawY)},
          ];
          if (coMoveIds.length > 0) {
            requirements
              .filter((r) => coMoveIds.includes(r.id))
              .forEach((r) => {
                moves.push({
                  id: r.id,
                  x: Math.round((r.x ?? 0) + dx),
                  y: Math.round((r.y ?? 0) + dy),
                });
              });
          }
          onElementMove(moves);
        }
        setDragState(null);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    // selectedIds / groupEditId / sentinelIds are read live from dragCtxRef.current.
    [elemRefs, dragCtxRef, onElementMove, onElementClick, fillMode, zoom, requirements],
  );
  return {dragState, startDrag};
}

/** Resize handles for absolute elements. */
export function useResize(opts: {
  elemRefs: ElemRefs;
  zoom: number;
  fillMode: boolean;
  onElementResize?: (id: string, x: number, y: number, width: number, height: number) => void;
}) {
  const {elemRefs, zoom, fillMode, onElementResize} = opts;
  const [resizeState, setResizeState] = useState<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const startResize = useCallback(
    (e: React.MouseEvent, item: PhotoRequirement, handle: ResizeHandle) => {
      if (!onElementResize || fillMode) return;
      e.preventDefault();
      e.stopPropagation();
      const startMouseX = e.clientX;
      const startMouseY = e.clientY;
      const el = elemRefs.current.get(item.id);
      const startW = item.width ?? el?.offsetWidth ?? 120;
      const startH = item.height ?? el?.offsetHeight ?? 40;
      const startX = item.x ?? 0;
      const startY = item.y ?? 0;

      const compute = (mx: number, my: number, round = false) => {
        const dx = (mx - startMouseX) / zoom;
        const dy = (my - startMouseY) / zoom;
        let x = startX,
          y = startY,
          width = startW,
          height = startH;
        if (handle === 'nw' || handle === 'w' || handle === 'sw') {
          const edx = Math.min(dx, startW - 40);
          width = startW - edx;
          x = startX + edx;
        } else if (handle === 'ne' || handle === 'e' || handle === 'se') {
          width = Math.max(40, startW + dx);
        }
        if (handle === 'nw' || handle === 'n' || handle === 'ne') {
          const edy = Math.min(dy, startH - 20);
          height = startH - edy;
          y = startY + edy;
        } else if (handle === 'sw' || handle === 's' || handle === 'se') {
          height = Math.max(20, startH + dy);
        }
        return round
          ? {
              x: Math.round(x),
              y: Math.round(y),
              width: Math.round(width),
              height: Math.round(height),
            }
          : {x, y, width, height};
      };

      setResizeState({id: item.id, ...compute(startMouseX, startMouseY)});
      const onMove = (me: MouseEvent) =>
        setResizeState({id: item.id, ...compute(me.clientX, me.clientY)});
      const onUp = (me: MouseEvent) => {
        const r = compute(me.clientX, me.clientY, true);
        onElementResize(item.id, r.x, r.y, r.width, r.height);
        setResizeState(null);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [elemRefs, onElementResize, fillMode, zoom],
  );
  return {resizeState, startResize};
}

/** Marquee (rubber-band) multi-select over a page. */
export function useMarquee(opts: {
  elemRefs: ElemRefs;
  zoom: number;
  fillMode: boolean;
  requirements: PhotoRequirement[];
  onSelectionChange?: (ids: string[], opts?: {additive?: boolean}) => void;
  onPageFocus?: (pageIdx: number) => void;
}) {
  const {elemRefs, zoom, fillMode, requirements, onSelectionChange, onPageFocus} = opts;
  const [marquee, setMarquee] = useState<{
    pageIdx: number;
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null>(null);
  const startMarquee = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, pageIdx: number) => {
      if (e.button !== 0 || fillMode) return;
      // Suppress native text selection so rubber-banding doesn't highlight the text
      // of elements the marquee sweeps over.
      e.preventDefault();
      onPageFocus?.(pageIdx);
      blurActiveElement();
      document.body.style.userSelect = 'none';
      window.getSelection?.()?.removeAllRanges();
      const additive = e.ctrlKey || e.metaKey || e.shiftKey;
      const pageRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const x0 = (e.clientX - pageRect.left) / zoom;
      const y0 = (e.clientY - pageRect.top) / zoom;
      setMarquee({pageIdx, x0, y0, x1: x0, y1: y0});
      let lastX1 = x0,
        lastY1 = y0;

      const computeIntersected = (x1: number, y1: number): string[] => {
        const mLeft = Math.min(x0, x1);
        const mTop = Math.min(y0, y1);
        const mRight = Math.max(x0, x1);
        const mBottom = Math.max(y0, y1);
        const ids: string[] = [];
        for (const req of requirements) {
          if (req.type === 'group') continue; // sentinels are not hit-testable
          const el = elemRefs.current.get(req.id);
          if (!el) continue;
          const r = el.getBoundingClientRect();
          const elLeft = (r.left - pageRect.left) / zoom;
          const elTop = (r.top - pageRect.top) / zoom;
          const elRight = elLeft + r.width / zoom;
          const elBottom = elTop + r.height / zoom;
          if (elLeft < mRight && elRight > mLeft && elTop < mBottom && elBottom > mTop) {
            ids.push(req.id);
          }
        }
        return ids;
      };

      const onMove = (me: MouseEvent) => {
        lastX1 = (me.clientX - pageRect.left) / zoom;
        lastY1 = (me.clientY - pageRect.top) / zoom;
        setMarquee({pageIdx, x0, y0, x1: lastX1, y1: lastY1});
        if (Math.abs(lastX1 - x0) > 4 || Math.abs(lastY1 - y0) > 4) {
          onSelectionChange?.(computeIntersected(lastX1, lastY1), {additive});
        }
      };

      const onUp = () => {
        setMarquee(null);
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        const draggedFar = Math.abs(lastX1 - x0) > 4 || Math.abs(lastY1 - y0) > 4;
        if (draggedFar) {
          onSelectionChange?.(computeIntersected(lastX1, lastY1), {additive});
          const suppressNextClick = (ev: Event) => {
            ev.stopImmediatePropagation();
            ev.preventDefault();
            window.removeEventListener('click', suppressNextClick, true);
          };
          window.addEventListener('click', suppressNextClick, true);
        }
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [elemRefs, fillMode, zoom, onSelectionChange, requirements, onPageFocus],
  );
  return {marquee, startMarquee};
}
